import { createContext, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { login as loginRequest, type ApiAuthUser } from '@/services/authApi';
import { updateCurrentUser, type UpdateCurrentUserPayload } from '@/services/userApi';
import { clearStoredAuth, readStoredAuth, writeStoredAuth } from '@/auth/authStorage';

interface AuthContextValue {
  isAuthenticated: boolean;
  user: ApiAuthUser | null;
  accessToken: string | null;
  login: (email: string, password: string) => Promise<ApiAuthUser>;
  updateProfile: (payload: UpdateCurrentUserPayload) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const stored = useMemo(() => readStoredAuth(), []);
  const [user, setUser] = useState<ApiAuthUser | null>(stored?.user ?? null);
  const [accessToken, setAccessToken] = useState<string | null>(stored?.accessToken ?? null);

  const login = async (email: string, password: string) => {
    const response = await loginRequest({ username: email, password });
    writeStoredAuth({
      accessToken: response.access_token,
      refreshToken: response.refresh_token,
      user: response.user,
    });
    setAccessToken(response.access_token);
    setUser(response.user);

    return response.user;
  };

  const updateProfile = async (payload: UpdateCurrentUserPayload) => {
    if (!user || !accessToken) {
      throw new Error('Brak aktywnej sesji użytkownika.');
    }

    const updatedUser = await updateCurrentUser(payload);
    const currentStored = readStoredAuth();
    if (currentStored?.accessToken && currentStored.refreshToken) {
      writeStoredAuth({
        ...currentStored,
        user: updatedUser,
      });
    }
    setUser(updatedUser);
  };

  const logout = () => {
    clearStoredAuth();
    setAccessToken(null);
    setUser(null);
  };

  const value = useMemo<AuthContextValue>(() => ({
    isAuthenticated: Boolean(accessToken && user),
    user,
    accessToken,
    login,
    updateProfile,
    logout,
  }), [accessToken, login, logout, updateProfile, user]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }

  return context;
}